import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Checkbox,
  DatePicker,
  Input,
  InputNumber,
  Radio,
  Select,
  message,
  Popover,
} from "antd";
import { DollarInputNumber, sumMoney } from "./inputNumberCustom";
import { connect } from "react-redux";
import { updateStatus } from "../Store/status";
import { NeedUpdateLenderLoanForm } from "../Store/actions";
import "./lenderLoanForm.css";
import dayjs from "dayjs";

// API 接口函数
const fetchLenderLoanFormData = async (applicationId: string) => {
  console.log("fetchLenderLoanFormData called", { applicationId });
  try {
    const response = await fetch(`/los/getlenderloanform`, {
      // /${applicationId}
      method: "POST",
      headers: {
        Authorization: "Bearer " + sessionStorage.getItem("ZeitroA"),
        Cache: "no-cache",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        dataType: "all",
        loanId: applicationId,
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to fetch data");
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching form data:", error);
    throw error;
  }
};

// 更新接口参数类型
interface UpdateFormDataParams {
  data: any;
  loanId: string;
  dataType: string;
}

const updateLenderLoanFormData = async (
  applicationId: string,
  params: UpdateFormDataParams
) => {
  try {
    const response = await fetch(`/los/updatelenderloanform`, {
      method: "POST",
      headers: {
        Authorization: "Bearer " + sessionStorage.getItem("ZeitroA"),
        Cache: "no-cache",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      throw new Error("Failed to update data");
    }
    console.log("response", response);

    // return await response.json();

    if (response.status === 200) {
      return true;
    } else {
      return false;
    }
  } catch (error) {
    console.error("Error updating form data:", error);
    throw error;
  }
};

// 全局变量来跟踪是否已经加载过数据（用于处理 React.StrictMode）
let globalLoadingState: { [key: string]: boolean } = {};

// 防抖函数
const useDebounce = (callback: Function, delay: number) => {
  const timeoutRef = useRef<NodeJS.Timeout>();

  return useCallback(
    (...args: any[]) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    },
    [callback, delay]
  );
};

// 获取禁用状态的classname辅助函数
const getDisabledClassName = (elementType: string, isDisabled: boolean) => {
  if (!isDisabled) return "";

  switch (elementType) {
    case "input":
      return "input-disabled";
    case "select":
      return "select-disabled";
    case "datepicker":
      return "datepicker-disabled";
    case "inputnumber":
      return "inputnumber-disabled";
    case "checkbox":
      return "checkbox-disabled";
    case "radio":
      return "radio-disabled";
    case "checkboxgroup":
      return "checkboxgroup-disabled";
    case "radiogroup":
      return "radiogroup-disabled";
    case "dollarinputnumber":
      return "dollarinputnumber-disabled";
    default:
      return "";
  }
};

// 定义组件的 props 类型
interface LenderLoanFormProps {
  currentView?: string;
  editMode?: boolean;
  application?: any;
  status?: string;
  statusSource?: string;
  updateStatus: (status: string, source?: string) => void;
  needUpdateLenderLoanForm?: boolean;
  resetNeedUpdateLenderLoanForm: () => void;
}

const LenderLoanForm = React.memo(function LenderLoanForm({ currentView, editMode, application, status, statusSource, updateStatus, needUpdateLenderLoanForm, resetNeedUpdateLenderLoanForm }: LenderLoanFormProps) {
  // 渲染计数器（仅在开发环境）
  const renderCount = useRef(0);
  renderCount.current += 1;
  
  if (process.env.NODE_ENV === 'development') {
    // console.log(`LenderLoanForm rendered ${renderCount.current} times`);
  }



  // 从 URL path 获取 loanId
  const pathname = window.location.pathname;
  const loanId = useMemo(() => {
    const id = pathname.substring(pathname.lastIndexOf("/") + 1);
    console.log("loanId calculated", { pathname, id });
    return id;
  }, [pathname]);

  // 加载状态
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [changedSections, setChangedSections] = useState<Set<string>>(
    new Set()
  );
  
  // 使用 ref 来跟踪初始化状态，避免闭包问题
  const initializedRef = useRef(false);
  const isInitialLoadRef = useRef(true);

  const Line = () => {
    return (
      <div
        style={{
          width: "100%",
          height: 1,
          backgroundColor: "#E0E0E0",
          margin: "28px 0",
        }}
      ></div>
    );
  };

  const [transactionDetail, setTransactionDetail] = useState("");
  const [isRefinance, setIsRefinance] = useState(false);
  const [otherRefinanceProgramExplain, setOtherRefinanceProgramExplain] =
    useState("");

  const [names, setNames] = useState<string[]>([]);
  const [names_refinance, setNames_refinance] = useState<string[]>([]);

  const [estateType, setEstateType] = useState("fee-simple");
  const [isRefinanceLoan_L2, setIsRefinanceLoan_L2] = useState(false);

  const [amortizationType, setAmortizationType] = useState("fixed-rate");
  const [mortgageType, setMortgageType] = useState("");
  const [otherMortgageType, setOtherMortgageType] = useState("");
  const [otherAmortizationExplain, setOtherAmortizationExplain] = useState("");

  // L1
  const [communityPropertyState, setCommunityPropertyState] = useState<
    string[]
  >([]);
  const [projectType, setProjectType] = useState(
    "property-not-located-in-a-project"
  );
  const [refinanceProgram, setRefinanceProgram] = useState("");

  // L1 新增状态
  const [closingType, setClosingType] = useState("");
  const [constructionCost, setConstructionCost] = useState<number | null>(null);
  const [lotAcquiredDate, setLotAcquiredDate] = useState<any>(null);
  const [originalLotCost, setOriginalLotCost] = useState<number | null>(null);
  const [refinanceType, setRefinanceType] = useState("");
  const [energyImprovements, setEnergyImprovements] = useState<string[]>([]); // from 1003 5a question E

  // L2 新增状态
  const [titleHeldNames, setTitleHeldNames] = useState<string[]>([]);
  const [leaseholdExpirationDate, setLeaseholdExpirationDate] =
    useState<any>(null);
  const [titleManner, setTitleManner] = useState("");
  const [trustInformation, setTrustInformation] = useState("");
  const [indianLandTenure, setIndianLandTenure] = useState("");

  // L3 新增状态
  const [interestOnlyTerm, setInterestOnlyTerm] = useState<number | null>(null);
  const [subsequentAdjustmentPeriod, setSubsequentAdjustmentPeriod] = useState<
    number | null
  >(null);
  const [loanFeatures, setLoanFeatures] = useState<string[]>([]);
  const [balloonTerm, setBalloonTerm] = useState<number | null>(0);
  const [interestOnlyTermFeature, setInterestOnlyTermFeature] = useState<
    number | null
  >(0);
  const [negativeAmortizationTerm, setNegativeAmortizationTerm] = useState<
    number | null
  >(0);
  const [prepaymentPenaltyTerm, setPrepaymentPenaltyTerm] = useState<
    number | null
  >(0);
  const [buydownRate, setBuydownRate] = useState<number | null>(null);
  const [otherLoanFeature, setOtherLoanFeature] = useState("");
  const [otherLoanFeatureTerm, setOtherLoanFeatureTerm] = useState<
    number | null
  >(null);
  const [noteRate, setNoteRate] = useState<number | null>(null);
  const [loanTerm, setLoanTerm] = useState<number | null>(null);
  const [mortgageLienType, setMortgageLienType] = useState("");
  const [firstMortgagePayment, setFirstMortgagePayment] = useState<
    number | null
  >(null);
  const [subordinateLienPayment, setSubordinateLienPayment] = useState<
    number | null
  >(null);
  const [homeownersInsurance, setHomeownersInsurance] = useState<number | null>(
    null
  );
  const [supplementalInsurance, setSupplementalInsurance] = useState<
    number | null
  >(null);
  const [propertyTaxes, setPropertyTaxes] = useState<number | null>(null);
  const [mortgageInsurance, setMortgageInsurance] = useState<number | null>(
    null
  );
  const [associationDues, setAssociationDues] = useState<number | null>(null);
  const [otherMonthlyExpenses, setOtherMonthlyExpenses] = useState<
    number | null
  >(null);
  const [totalMonthlyPayment, setTotalMonthlyPayment] = useState<number | null>(
    null
  );

  // L4 新增状态
  const [salesContractPrice, setSalesContractPrice] = useState<number | null>(
    null
  );
  const [improvementsCost, setImprovementsCost] = useState<number | null>(null);
  const [landCost, setLandCost] = useState<number | null>(null);
  const [refinanceBalance, setRefinanceBalance] = useState<number | null>(null);
  const [creditCardsDebts, setCreditCardsDebts] = useState<number | null>(null);
  const [borrowerClosingCosts, setBorrowerClosingCosts] = useState<
    number | null
  >(null);
  const [discountPoints, setDiscountPoints] = useState<number | null>(null);
  const [totalDueFromBorrower, setTotalDueFromBorrower] = useState<
    number | null
  >(null);
  const [loanAmountExcludingInsurance, setLoanAmountExcludingInsurance] =
    useState<number | null>(null);
  const [financedInsuranceAmount, setFinancedInsuranceAmount] = useState<
    number | null
  >(null);
  const [otherNewMortgageLoans, setOtherNewMortgageLoans] = useState<
    number | null
  >(null);
  const [totalMortgageLoans, setTotalMortgageLoans] = useState<number | null>(
    null
  );
  const [sellerCredits, setSellerCredits] = useState<number | null>(null);
  const [otherCredits, setOtherCredits] = useState<number | null>(null);
  const [totalCredits, setTotalCredits] = useState<number | null>(null);
  const [calculationTotalDue, setCalculationTotalDue] = useState<number | null>(
    null
  );
  const [calculationLessTotal, setCalculationLessTotal] = useState<
    number | null
  >(null);
  const [cashFromToBorrower, setCashFromToBorrower] = useState<number | null>(
    null
  );

  // 计算汇总项的函数
  // H. TOTAL DUE FROM BORROWER(s) = A + B + C + D + E + F + G
  const calculateTotalDueFromBorrower = useCallback(() => {
    const values = [
      salesContractPrice,
      improvementsCost,
      landCost,
      refinanceBalance,
      creditCardsDebts,
      borrowerClosingCosts,
      discountPoints
    ];
    const total = sumMoney(values);
    setTotalDueFromBorrower(total);
    return total;
  }, [salesContractPrice, improvementsCost, landCost, refinanceBalance, creditCardsDebts, borrowerClosingCosts, discountPoints]);

  // K. TOTAL MORTGAGE LOANS = I + J
  const calculateTotalMortgageLoans = useCallback(() => {
    const values = [
      loanAmountExcludingInsurance,
      financedInsuranceAmount,
      otherNewMortgageLoans
    ];
    const total = sumMoney(values);
    setTotalMortgageLoans(total);
    return total;
  }, [loanAmountExcludingInsurance, financedInsuranceAmount, otherNewMortgageLoans]);

  // N. TOTAL CREDITS = L + M
  const calculateTotalCredits = useCallback(() => {
    const values = [
      sellerCredits,
      otherCredits
    ];
    const total = sumMoney(values);
    setTotalCredits(total);
    return total;
  }, [sellerCredits, otherCredits]);

  // 计算CALCULATION部分的字段
  // calculationTotalDue = H (TOTAL DUE FROM BORROWER)
  // calculationLessTotal = K + N (TOTAL MORTGAGE LOANS + TOTAL CREDITS)
  // cashFromToBorrower = H - (K + N)
  const calculateCalculationFields = useCallback(() => {
    // 计算 H 行
    const totalDue = calculateTotalDueFromBorrower();
    setCalculationTotalDue(totalDue);

    // 计算 K 行
    const totalMortgage = calculateTotalMortgageLoans();
    
    // 计算 N 行
    const totalCredits = calculateTotalCredits();
    
    // 计算 K + N
    const lessTotal = totalMortgage + totalCredits;
    setCalculationLessTotal(lessTotal);
    
    // 计算最终结果：H - (K + N)
    const cashResult = totalDue - lessTotal;
    setCashFromToBorrower(cashResult);
  }, [calculateTotalDueFromBorrower, calculateTotalMortgageLoans, calculateTotalCredits]);

  // 初始化完成后，计算一次汇总字段
  useEffect(() => {
    if (initializedRef.current && !isInitialLoadRef.current) {
      // 初始化完成后，计算一次汇总字段
      calculateCalculationFields();
    }
  }, [initialized, isInitialLoad, calculateCalculationFields]);

  // 监听依赖项变化，自动计算汇总（仅在用户修改数据时）
  useEffect(() => {
    if (initializedRef.current && !isInitialLoadRef.current) {
      calculateCalculationFields();
    }
  }, [
    salesContractPrice,
    improvementsCost,
    landCost,
    refinanceBalance,
    creditCardsDebts,
    borrowerClosingCosts,
    discountPoints,
    loanAmountExcludingInsurance,
    financedInsuranceAmount,
    otherNewMortgageLoans,
    sellerCredits,
    otherCredits,
    calculateCalculationFields
  ]);

  // 数据获取函数
  const loadFormData = useCallback(async () => {
    console.log("loadFormData called", { 
      loanId, 
      initializedRef: initializedRef.current,
      isInitialLoadRef: isInitialLoadRef.current 
    });
    if (!loanId) {
      console.warn("No loanId found, skipping data loading");
      return;
    }

    setLoading(true);
    try {
      const data = await fetchLenderLoanFormData(loanId);

      // console.log("data------------", data);
      data.L1 = data.l1;
      data.L2 = data.l2;
      data.L3 = data.l3;
      data.L4 = data.l4;

      // 设置所有状态
      if (data.L1) {
        // console.log("data.L1------------", data.L1);
        // console.log("data.L1.isRefinance------------", data.L1.isRefinance);
        setCommunityPropertyState(data.L1.communityPropertyState || []);
        setTransactionDetail(data.L1.transactionDetail || "");
        setClosingType(data.L1.closingType || "");
        setConstructionCost(data.L1.constructionCost || null);
        setLotAcquiredDate(
          data.L1.lotAcquiredDate ? dayjs(data.L1.lotAcquiredDate).format("MM/DD/YYYY") : null
        );
        setOriginalLotCost(data.L1.originalLotCost || null);
        setIsRefinance(data.L1.isRefinance || false);
        setRefinanceType(data.L1.refinanceType || "");
        setRefinanceProgram(data.L1.refinanceProgram || "");
        setOtherRefinanceProgramExplain(
          data.L1.otherRefinanceProgramExplain || ""
        );
        setEnergyImprovements(data.L1.energyImprovements || []);
        setProjectType(
          data.L1.projectType || "property-not-located-in-a-project"
        );
      }

      if (data.L2) {
        // console.log("data.L2------------", data.L2);
        // console.log("data.L2.isRefinanceLoan------------", data.L2.isRefinanceLoan);
        setNames(data.L2.names || []);
        setEstateType(data.L2.estateType || "fee-simple");
        setLeaseholdExpirationDate(
          data.L2.leaseholdExpirationDate
            ? dayjs(data.L2.leaseholdExpirationDate).format("MM/DD/YYYY")
            : null
        );
        setTitleManner(data.L2.titleManner || "");
        setIsRefinanceLoan_L2(data.L2.isRefinanceLoan || false);
        setNames_refinance(data.L2.namesRefinance || []);
        setTrustInformation(data.L2.trustInformation || "");
        setIndianLandTenure(data.L2.indianLandTenure || "");
      }

      if (data.L3) {
        setMortgageType(data.L3.mortgageType || "");
        setOtherMortgageType(data.L3.otherMortgageType || "");
        setAmortizationType(data.L3.amortizationType || "fixed-rate");
        setOtherAmortizationExplain(data.L3.otherAmortizationExplain || "");
        setInterestOnlyTerm(data.L3.interestOnlyTerm || null);
        setSubsequentAdjustmentPeriod(
          data.L3.subsequentAdjustmentPeriod || null
        );
        setLoanFeatures(data.L3.loanFeatures || []);
        setBalloonTerm(data.L3.balloonTerm || null);
        setInterestOnlyTermFeature(data.L3.interestOnlyTermFeature || null);
        setNegativeAmortizationTerm(data.L3.negativeAmortizationTerm || null);
        setPrepaymentPenaltyTerm(data.L3.prepaymentPenaltyTerm || null);
        setBuydownRate(data.L3.buydownRate || null);
        setOtherLoanFeature(data.L3.otherLoanFeature || "");
        setOtherLoanFeatureTerm(data.L3.otherLoanFeatureTerm || null);
        setNoteRate(data.L3.noteRate || null);
        setLoanTerm(data.L3.loanTerm || null);
        setMortgageLienType(data.L3.mortgageLienType || "");
        setFirstMortgagePayment(data.L3.firstMortgagePayment ?? null);
        setSubordinateLienPayment(data.L3.subordinateLienPayment ?? null);
        setHomeownersInsurance(data.L3.homeownersInsurance ?? null);
        setSupplementalInsurance(data.L3.supplementalInsurance ?? null);
        setPropertyTaxes(data.L3.propertyTaxes ?? null);
        setMortgageInsurance(data.L3.mortgageInsurance ?? null);
        setAssociationDues(data.L3.associationDues ?? null);
        setOtherMonthlyExpenses(data.L3.otherMonthlyExpenses ?? null);
        setTotalMonthlyPayment(
          sumMoney([
            data.L3.firstMortgagePayment,
            data.L3.subordinateLienPayment,
            data.L3.homeownersInsurance,
            data.L3.supplementalInsurance,
            data.L3.propertyTaxes,
            data.L3.mortgageInsurance,
            data.L3.associationDues,
            data.L3.otherMonthlyExpenses,
          ])
        );
      }

      if (data.L4) {
        setSalesContractPrice(data.L4.salesContractPrice ?? null);
        setImprovementsCost(data.L4.improvementsCost ?? null);
        setLandCost(data.L4.landCost ?? null);
        setRefinanceBalance(data.L4.refinanceBalance ?? null);
        setCreditCardsDebts(data.L4.creditCardsDebts ?? null);
        setBorrowerClosingCosts(data.L4.borrowerClosingCosts ?? null);
        setDiscountPoints(data.L4.discountPoints ?? null);
        const totalDue = sumMoney([
          data.L4.salesContractPrice,
          data.L4.improvementsCost,
          data.L4.landCost,
          data.L4.refinanceBalance,
          data.L4.creditCardsDebts,
          data.L4.borrowerClosingCosts,
          data.L4.discountPoints,
        ]);
        setTotalDueFromBorrower(totalDue);
        setLoanAmountExcludingInsurance(
          data.L4.loanAmountExcludingInsurance ?? null
        );
        setFinancedInsuranceAmount(data.L4.financedInsuranceAmount ?? null);
        setOtherNewMortgageLoans(data.L4.otherNewMortgageLoans ?? null);
        const totalMortgage = sumMoney([
          data.L4.loanAmountExcludingInsurance,
          data.L4.financedInsuranceAmount,
          data.L4.otherNewMortgageLoans,
        ]);
        setTotalMortgageLoans(totalMortgage);
        setSellerCredits(data.L4.sellerCredits ?? null);
        setOtherCredits(data.L4.otherCredits ?? null);
        const totalCredits = sumMoney([
          data.L4.sellerCredits,
          data.L4.otherCredits,
        ]);
        setTotalCredits(totalCredits);
        setCalculationTotalDue(totalDue);
        const lessTotal = totalMortgage + totalCredits;
        setCalculationLessTotal(lessTotal);
        setCashFromToBorrower(totalDue - lessTotal);
      }

      console.log("Before setting initialized=true, isInitialLoad=false", {
        initializedRef: initializedRef.current,
        isInitialLoadRef: isInitialLoadRef.current
      });
      setInitialized(true);
      setIsInitialLoad(false);
      initializedRef.current = true;
      isInitialLoadRef.current = false;
      console.log("After setting initialized=true, isInitialLoad=false", {
        initializedRef: initializedRef.current,
        isInitialLoadRef: isInitialLoadRef.current
      });
      
      // 延迟计算汇总项，确保所有状态都已设置
      // 注意：这里不调用 calculateCalculationFields()，让 useEffect 来处理
      // 避免在数据加载时触发不必要的计算和保存
    } catch (error) {
      console.error("Failed to load form data:", error);
      message.error("Failed to load form data");
      // 重置全局加载状态，允许重试
      if (loanId) {
        globalLoadingState[loanId] = false;
      }
    } finally {
      setLoading(false);
    }
  }, [loanId]);

  // 获取当前表单数据的函数
  const getCurrentFormData = useCallback((): LenderLoanFormData => {
    return {
      L1: {
        communityPropertyState,
        transactionDetail,
        closingType,
        constructionCost,
        lotAcquiredDate: lotAcquiredDate ? dayjs(lotAcquiredDate, "MM/DD/YYYY").format("YYYY-MM-DD") : null,
        originalLotCost,
        isRefinance,
        refinanceType,
        refinanceProgram,
        otherRefinanceProgramExplain,
        energyImprovements,
        projectType,
      },
      L2: {
        names,
        estateType,
        leaseholdExpirationDate:
          leaseholdExpirationDate ? dayjs(leaseholdExpirationDate, "MM/DD/YYYY").format("YYYY-MM-DD") : null,
        titleManner,
        isRefinanceLoan: isRefinanceLoan_L2,
        namesRefinance: names_refinance,
        trustInformation,
        indianLandTenure,
      },
      L3: {
        mortgageType,
        otherMortgageType,
        amortizationType,
        otherAmortizationExplain,
        interestOnlyTerm,
        subsequentAdjustmentPeriod,
        loanFeatures,
        balloonTerm,
        interestOnlyTermFeature,
        negativeAmortizationTerm,
        prepaymentPenaltyTerm,
        buydownRate,
        otherLoanFeature,
        otherLoanFeatureTerm,
        noteRate,
        loanTerm,
        mortgageLienType,
        firstMortgagePayment,
        subordinateLienPayment,
        homeownersInsurance,
        supplementalInsurance,
        propertyTaxes,
        mortgageInsurance,
        associationDues,
        otherMonthlyExpenses,
        totalMonthlyPayment,
      },
      L4: {
        salesContractPrice,
        improvementsCost,
        landCost,
        refinanceBalance,
        creditCardsDebts,
        borrowerClosingCosts,
        discountPoints,
        totalDueFromBorrower,
        loanAmountExcludingInsurance,
        financedInsuranceAmount,
        otherNewMortgageLoans,
        totalMortgageLoans,
        sellerCredits,
        otherCredits,
        totalCredits,
        calculationTotalDue,
        calculationLessTotal,
        cashFromToBorrower,
      },
    };
  }, [
    communityPropertyState,
    transactionDetail,
    closingType,
    constructionCost,
    lotAcquiredDate,
    originalLotCost,
    isRefinance,
    refinanceType,
    refinanceProgram,
    otherRefinanceProgramExplain,
    energyImprovements,
    projectType,
    names,
    estateType,
    leaseholdExpirationDate,
    titleManner,
    isRefinanceLoan_L2,
    names_refinance,
    trustInformation,
    indianLandTenure,
    mortgageType,
    otherMortgageType,
    amortizationType,
    otherAmortizationExplain,
    interestOnlyTerm,
    subsequentAdjustmentPeriod,
    loanFeatures,
    balloonTerm,
    interestOnlyTermFeature,
    negativeAmortizationTerm,
    prepaymentPenaltyTerm,
    buydownRate,
    otherLoanFeature,
    otherLoanFeatureTerm,
    noteRate,
    loanTerm,
    mortgageLienType,
    firstMortgagePayment,
    subordinateLienPayment,
    homeownersInsurance,
    supplementalInsurance,
    propertyTaxes,
    mortgageInsurance,
    associationDues,
    otherMonthlyExpenses,
    totalMonthlyPayment,
    salesContractPrice,
    improvementsCost,
    landCost,
    refinanceBalance,
    creditCardsDebts,
    borrowerClosingCosts,
    discountPoints,
    totalDueFromBorrower,
    loanAmountExcludingInsurance,
    financedInsuranceAmount,
    otherNewMortgageLoans,
    totalMortgageLoans,
    sellerCredits,
    otherCredits,
    totalCredits,
    calculationTotalDue,
    calculationLessTotal,
    cashFromToBorrower,
  ]);

  // 标记数据变化的函数
  const markSectionChanged = useCallback(
    (section: string) => {
      if (initializedRef.current && !isInitialLoadRef.current) {
        setChangedSections((prev) => new Set(prev).add(section));
      }
    },
    []
  );

  // 保存特定部分的函数
  const saveSection = useCallback(
    async (section: string) => {
      console.log(`saveSection called for ${section}`, { loanId, initialized: initializedRef.current, editMode });
      
      // 如果是预览模式，不调用更新接口
      if (!editMode) {
        console.log(`saveSection skipped for ${section} - preview mode`);
        return;
      }
      
      if (!loanId) {
      // if (!loanId || !initialized) {
        console.log(`saveSection early return for ${section}`);
        return;
      }

      setSaving(true);
      // 标记正在保存 lender loan form 数据
      isSavingLenderLoanForm.current = true;
      updateStatus("saving", "lenderLoanForm");
      try {
        const formData = getCurrentFormData();
        console.log(`saveSection formData for ${section}:`, formData);
        let sectionData: any;
        let dataType: string;

        switch (section) {
          case "L1":
            sectionData = formData.L1;
            dataType = "l1";
            console.log(`saveSection L1 data:`, sectionData);
            break;
          case "L2":
            sectionData = formData.L2;
            dataType = "l2";
            console.log(`saveSection L2 data:`, sectionData);
            break;
          case "L3":
            sectionData = formData.L3;
            dataType = "l3";
            break;
          case "L4":
            sectionData = formData.L4;
            dataType = "l4";
            break;
          default:
            return;
        }

        const updateResult = await updateLenderLoanFormData(loanId, {
          data: sectionData,
          loanId,
          dataType,
        });

        if (updateResult) {
          console.log(`${section} data auto-saved successfully`);
          setChangedSections((prev) => {
            const newSet = new Set(prev);
            newSet.delete(section);
            return newSet;
          });
          updateStatus("saved", "lenderLoanForm");
          
          // 不再重新获取数据，避免循环问题
          // await loadFormData(true);
        } else {
          throw new Error(`Failed to update ${section} data`);
        }
      } catch (error) {
        console.error(`Auto-save ${section} failed:`, error);
        message.error(`Auto-save ${section} failed`);
        updateStatus("error", "lenderLoanForm");
      } finally {
        setSaving(false);
        // 重置标志
        isSavingLenderLoanForm.current = false;
      }
    },
    [loanId, getCurrentFormData, updateStatus, editMode]
  );

  // 稳定的保存函数
  const saveL1 = useCallback(() => saveSection("L1"), [saveSection]);
  const saveL2 = useCallback(() => saveSection("L2"), [saveSection]);
  const saveL3 = useCallback(() => saveSection("L3"), [saveSection]);
  const saveL4 = useCallback(() => saveSection("L4"), [saveSection]);

  // 防抖的自动保存
  const debouncedSaveL1 = useDebounce(saveL1, 1000);
  const debouncedSaveL2 = useDebounce(saveL2, 1000);
  const debouncedSaveL3 = useDebounce(saveL3, 1000);
  const debouncedSaveL4 = useDebounce(saveL4, 1000);
  const debouncedLoadFormData = useDebounce(loadFormData, 1000);

  // 使用 ref 来跟踪当前 loanId 是否已经加载过数据
  const loadedLoanIdRef = useRef<string | null>(null);

  // application 变化时 重新获取数据（防抖处理）
  // 监听1003表单数据变化
  // useEffect(() => {
  //   if (application) {
  //     // console.log("application changed", application.selection.loansize);
  //     // debouncedLoadFormData();
  //   }
  // }, [
  //   application.property.appraisal, // 1003 4a-property value => sales-contract-price
  //   application.property.salesprice, // 1003 4a-property value => sales-contract-price
  //   application.selection.loansize, // 1003 4a-property value => loanAmountExcludingInsurance
  //   application.borrower.declarations.cleanenergylien, // 1003 5a-E => clean-energy-lien
  //   application.coborrower.declarations.cleanenergylien, // 1003 5a-E => clean-energy-lien
  //   application.property.purpose, // 1003 4a-loan purpose => isRefinance & isRefinanceLoan_L2
  //   // application.property.loansize, // 1003 4a-property value => loansize
  //   application.borrower.ownedproperties, // 1003 3a 3b 3c => ownedproperties
  //   application.coborrower.ownedproperties, // 1003 3a 3b 3c => ownedproperties
  // ]);

  // 使用 ref 来跟踪是否正在保存 lender loan form 数据
  const isSavingLenderLoanForm = useRef(false);
  
  // 配置选项 - 可以根据实际情况调整
  const CONFIG = {
    maxRetries: 2, // 最大重试次数（减少重试次数）
    initialDelay: 0, // 初始延迟（毫秒）- 移除延迟，直接获取
    retryInterval: 1000, // 重试间隔（毫秒）- 从3秒减少到1秒
    dataCheckDelay: 200, // 数据检查延迟（毫秒）- 从0.5秒减少到0.2秒
  };
  
  // 监听保存状态变化，当1003数据保存完成后重新获取lender loan form数据
  const previousStatus = useRef(status);
  const retryCountRef = useRef(0);
  const lastDataHashRef = useRef<string>(''); // 用于检测数据是否真的发生了变化
  
  // 监听 needUpdateLenderLoanForm 变化，决定是否重新获取数据
  useEffect(() => {
    if (needUpdateLenderLoanForm && loanId && initializedRef.current) {
      console.log("✅ needUpdateLenderLoanForm 为 true，准备重新获取 lender loan form 数据");
      
      // 重置重试计数
      retryCountRef.current = 0;
      
      // 直接获取数据（因为初始延迟为0）
      console.log(`🔄 立即获取lender loan form数据 (重试次数: ${retryCountRef.current})`);
      loadFormDataWithRetry();
      
      // 设置重试机制，确保数据是最新的
      const retryTimer = setInterval(() => {
        retryCountRef.current++;
        if (retryCountRef.current < CONFIG.maxRetries) {
          console.log(`🔄 第 ${retryCountRef.current} 次重试获取lender loan form数据`);
          loadFormDataWithRetry();
        } else {
          console.log("✅ 完成所有重试，停止获取lender loan form数据");
          clearInterval(retryTimer);
          retryCountRef.current = 0; // 重置重试计数
          // 重置标志
          resetNeedUpdateLenderLoanForm();
        }
      }, CONFIG.retryInterval); // 重试间隔
      
      // 清理定时器
      return () => {
        clearInterval(retryTimer);
      };
    }
  }, [needUpdateLenderLoanForm, loanId, resetNeedUpdateLenderLoanForm]);

  // 监听状态变化（保留原有逻辑作为备用）
  useEffect(() => {
    // 当状态从 "saving" 变为 "saved" 时，说明1003数据已经保存完成
    // 但是要排除 lender loan form 自己的保存操作
    if (previousStatus.current === "saving" && status === "saved" && statusSource !== "lenderLoanForm") {
      console.log("⚠️ 使用 status 变化触发数据获取（备用机制）");
      if (loanId && initializedRef.current) {
        // 重置重试计数
        retryCountRef.current = 0;
        
        // 直接获取数据（因为初始延迟为0）
        console.log(`🔄 立即获取lender loan form数据 (重试次数: ${retryCountRef.current})`);
        loadFormDataWithRetry();
        
        // 设置重试机制，确保数据是最新的
        const retryTimer = setInterval(() => {
          retryCountRef.current++;
          if (retryCountRef.current < CONFIG.maxRetries) {
            console.log(`🔄 第 ${retryCountRef.current} 次重试获取lender loan form数据`);
            loadFormDataWithRetry();
          } else {
            console.log("✅ 完成所有重试，停止获取lender loan form数据");
            clearInterval(retryTimer);
            retryCountRef.current = 0; // 重置重试计数
          }
        }, CONFIG.retryInterval); // 重试间隔
        
        // 清理定时器
        return () => {
          clearInterval(retryTimer);
        };
      }
    }
    
    previousStatus.current = status;
  }, [status, statusSource, loanId]);
  
  // 带重试逻辑的数据加载函数
  const loadFormDataWithRetry = useCallback(async () => {
    try {
      const currentDataHash = JSON.stringify(getCurrentFormData());
      console.log("📊 当前数据哈希:", currentDataHash.substring(0, 100) + "...");
      
      await loadFormData();
      
      // 检查数据是否真的发生了变化
      setTimeout(() => {
        const newDataHash = JSON.stringify(getCurrentFormData());
        console.log("📊 新数据哈希:", newDataHash.substring(0, 100) + "...");
        
        if (newDataHash !== lastDataHashRef.current) {
          console.log("✅ 数据已更新，停止重试");
          lastDataHashRef.current = newDataHash;
          retryCountRef.current = CONFIG.maxRetries; // 强制停止重试
        } else {
          console.log("⚠️ 数据未发生变化，继续重试");
        }
      }, CONFIG.dataCheckDelay); // 给一点时间让数据更新
      
    } catch (error) {
      console.error("❌ 加载数据失败:", error);
    }
  }, [loadFormData, getCurrentFormData]);

  // 组件挂载时加载数据
  useEffect(() => {
    console.log("useEffect triggered", { 
      loanId, 
      loadedLoanId: loadedLoanIdRef.current,
      globalLoadingState: globalLoadingState[loanId || ''],
      componentRenderCount: renderCount.current,
      initializedRef: initializedRef.current
    });
    
    // 如果 loanId 变化了，重置状态
    if (loanId && loadedLoanIdRef.current !== loanId) {
      console.log("loanId changed, resetting state", { 
        oldLoanId: loadedLoanIdRef.current, 
        newLoanId: loanId 
      });
      initializedRef.current = false;
      isInitialLoadRef.current = true;
      setInitialized(false);
      setIsInitialLoad(true);
      globalLoadingState[loanId] = false; // 重置全局加载状态
    }
    
    if (loanId && !globalLoadingState[loanId]) {
      console.log("Starting to load data", { loanId });
      globalLoadingState[loanId] = true;
      loadedLoanIdRef.current = loanId;
      loadFormData();
    } else if (loanId && globalLoadingState[loanId] && !initializedRef.current) {
      console.log("Already loading but not initialized, waiting...", { loanId });
    } else {
      console.log("Skipping data load", { 
        reason: !loanId ? "no loanId" : 
                globalLoadingState[loanId || ''] ? "already loading globally" : "unknown"
      });
    }
  }, [loanId]);

  // L1 数据变化时自动保存
  useEffect(() => {
    console.log("L1 useEffect triggered", {
      initialized,
      isInitialLoad,
      initializedRef: initializedRef.current,
      isInitialLoadRef: isInitialLoadRef.current,
      closingType,
      transactionDetail,
      condition: initializedRef.current && !isInitialLoadRef.current
    });
    
    // 添加一个简单的测试来验证状态
    if (initializedRef.current && !isInitialLoadRef.current) {
      console.log("✅ Condition met - calling debouncedSaveL1");
      debouncedSaveL1();
    } else {
      console.log("❌ Condition not met - not calling debouncedSaveL1", {
        initialized,
        isInitialLoad,
        initializedRef: initializedRef.current,
        isInitialLoadRef: isInitialLoadRef.current,
        condition: initializedRef.current && !isInitialLoadRef.current
      });
    }
  }, [
    communityPropertyState,
    transactionDetail,
    closingType,
    constructionCost,
    lotAcquiredDate,
    originalLotCost,
    isRefinance,
    refinanceType,
    refinanceProgram,
    otherRefinanceProgramExplain,
    energyImprovements,
    projectType,
  ]);

  // L2 数据变化时自动保存
  useEffect(() => {
    if (initializedRef.current && !isInitialLoadRef.current) {
      debouncedSaveL2();
    }
  }, [
    names,
    estateType,
    leaseholdExpirationDate,
    titleManner,
    isRefinanceLoan_L2,
    names_refinance,
    trustInformation,
    indianLandTenure,
  ]);

  // L3 数据变化时自动保存
  useEffect(() => {
    if (initializedRef.current && !isInitialLoadRef.current) {
      debouncedSaveL3();
    }
  }, [
    mortgageType,
    otherMortgageType,
    amortizationType,
    otherAmortizationExplain,
    interestOnlyTerm,
    subsequentAdjustmentPeriod,
    loanFeatures,
    balloonTerm,
    interestOnlyTermFeature,
    negativeAmortizationTerm,
    prepaymentPenaltyTerm,
    buydownRate,
    otherLoanFeature,
    otherLoanFeatureTerm,
    noteRate,
    loanTerm,
    mortgageLienType,
    firstMortgagePayment,
    subordinateLienPayment,
    homeownersInsurance,
    supplementalInsurance,
    propertyTaxes,
    mortgageInsurance,
    associationDues,
    otherMonthlyExpenses,
  ]);

  // 自动计算 totalMonthlyPayment
  useEffect(() => {
    if (initializedRef.current && !isInitialLoadRef.current) {
      const total = sumMoney([
        firstMortgagePayment,
        subordinateLienPayment,
        homeownersInsurance,
        supplementalInsurance,
        propertyTaxes,
        mortgageInsurance,
        associationDues,
        otherMonthlyExpenses,
      ]);

      setTotalMonthlyPayment(total);
    }
  }, [
    firstMortgagePayment,
    subordinateLienPayment,
    homeownersInsurance,
    supplementalInsurance,
    propertyTaxes,
    mortgageInsurance,
    associationDues,
    otherMonthlyExpenses,
  ]);

  // totalMonthlyPayment 变化时自动保存
  useEffect(() => {
    if (initializedRef.current && !isInitialLoadRef.current) {
      debouncedSaveL3();
    }
  }, [totalMonthlyPayment]);

  // L4 数据变化时自动保存
  useEffect(() => {
    if (initializedRef.current && !isInitialLoadRef.current) {
      debouncedSaveL4();
    }
  }, [
    salesContractPrice,
    improvementsCost,
    landCost,
    refinanceBalance,
    creditCardsDebts,
    borrowerClosingCosts,
    discountPoints,
    totalDueFromBorrower,
    loanAmountExcludingInsurance,
    financedInsuranceAmount,
    otherNewMortgageLoans,
    totalMortgageLoans,
    sellerCredits,
    otherCredits,
    totalCredits,
    // 移除计算字段的依赖，避免在数据加载时触发保存
    // calculationTotalDue,
    // calculationLessTotal,
    // cashFromToBorrower,
  ]);

  // 处理名字数组更新的函数
  const updateName = (
    index: number,
    value: string,
    isRefinance: boolean = false
  ) => {
    if (isRefinance) {
      const newNames = [...names_refinance];
      newNames[index] = value;
      setNames_refinance(newNames);
    } else {
      const newNames = [...names];
      newNames[index] = value;
      setNames(newNames);
    }
  };

  const dropdown_svg = () => {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="29" height="28" viewBox="0 0 29 28" fill="none">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M19.1888 12.8226L18.2607 11.8945L14.2396 15.9157L10.2201 11.8961L9.29199 12.8242L13.3115 16.8438L13.311 16.8443L14.2391 17.7724L14.2396 17.7718L14.2417 17.774L15.1698 16.8459L15.1677 16.8438L19.1888 12.8226Z" fill="#6E6E70"/>
      </svg>
    )
  }   

  const date_svg = () => {  
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="25" height="24" viewBox="0 0 25 24" fill="none">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M5.96482 5.5358C4.97537 5.65933 4.43265 5.88759 4.04195 6.25038C4.04195 6.25038 4.04196 6.25038 4.04195 6.25038M4.04195 6.25038C3.65126 6.61317 3.40543 7.11713 3.27241 8.0359C3.1359 8.97872 3.13399 10.2256 3.13399 12.0001V13.8573C3.13399 15.6318 3.1359 16.8787 3.27241 17.8215C3.40544 18.7403 3.65126 19.2442 4.04193 19.607L4.04196 19.607C4.43266 19.9698 4.97537 20.1981 5.96482 20.3216C6.98017 20.4484 8.32292 20.4501 10.234 20.4501H14.234C16.145 20.4501 17.4878 20.4484 18.5031 20.3216C19.4926 20.1981 20.0353 19.9698 20.426 19.607L20.426 19.607C20.8167 19.2442 21.0625 18.7403 21.1956 17.8215C21.3321 16.8787 21.334 15.6318 21.334 13.8573V12.0001C21.334 10.2256 21.3321 8.97872 21.1956 8.0359C21.0625 7.11713 20.8167 6.61318 20.426 6.25039L20.426 6.25036C20.0353 5.8876 19.4926 5.65933 18.5032 5.5358C17.4878 5.40904 16.145 5.40727 14.234 5.40727H10.234C8.32292 5.40727 6.98017 5.40904 5.96482 5.5358M10.1674 3.73584H14.3006C16.1296 3.73582 17.5937 3.7358 18.743 3.87928C19.932 4.02772 20.9179 4.3434 21.6988 5.06852C22.4797 5.79362 22.8196 6.70907 22.9795 7.81319C23.134 8.88035 23.134 10.2399 23.134 11.9383V13.9191C23.134 15.6175 23.134 16.977 22.9795 18.0442C22.8196 19.1483 22.4797 20.0638 21.6988 20.7889C20.9179 21.514 19.932 21.8297 18.743 21.9781C17.5937 22.1216 16.1296 22.1216 14.3005 22.1216H10.1675C8.33835 22.1216 6.87424 22.1216 5.72497 21.9781C4.53591 21.8297 3.55003 21.514 2.76915 20.7889C1.98827 20.0638 1.64832 19.1483 1.48846 18.0442C1.33394 16.977 1.33396 15.6175 1.33398 13.9191V11.9383C1.33396 10.2399 1.33394 8.88035 1.48846 7.81319C1.64832 6.70907 1.98828 5.79361 2.76916 5.0685C3.55004 4.34341 4.53592 4.02772 5.72498 3.87928C6.87423 3.7358 8.33832 3.73582 10.1674 3.73584Z" fill="#6E6E70"/>
        <path fill-rule="evenodd" clip-rule="evenodd" d="M7.23398 2.34277C7.73104 2.34277 8.13398 2.71694 8.13398 3.17849V4.57134C8.13398 5.0329 7.73104 5.40706 7.23398 5.40706C6.73693 5.40706 6.33398 5.0329 6.33398 4.57134V3.17849C6.33398 2.71694 6.73693 2.34277 7.23398 2.34277Z" fill="#6E6E70"/>
        <path fill-rule="evenodd" clip-rule="evenodd" d="M17.234 2.34277C17.731 2.34277 18.134 2.71694 18.134 3.17849V4.57134C18.134 5.0329 17.731 5.40706 17.234 5.40706C16.7369 5.40706 16.334 5.0329 16.334 4.57134V3.17849C16.334 2.71694 16.7369 2.34277 17.234 2.34277Z" fill="#6E6E70"/>
        <path fill-rule="evenodd" clip-rule="evenodd" d="M1.83398 9.21438C1.83398 8.75282 2.23693 8.37866 2.73398 8.37866H21.734C22.231 8.37866 22.634 8.75282 22.634 9.21438C22.634 9.67593 22.231 10.0501 21.734 10.0501H2.73398C2.23693 10.0501 1.83398 9.67593 1.83398 9.21438Z" fill="#6E6E70"/>
        <path d="M18.2344 16.6429C18.2344 17.1558 17.7867 17.5715 17.2344 17.5715C16.6821 17.5715 16.2344 17.1558 16.2344 16.6429C16.2344 16.1301 16.6821 15.7144 17.2344 15.7144C17.7867 15.7144 18.2344 16.1301 18.2344 16.6429Z" fill="#6E6E70"/>
        <path d="M18.2344 12.9286C18.2344 13.4414 17.7867 13.8571 17.2344 13.8571C16.6821 13.8571 16.2344 13.4414 16.2344 12.9286C16.2344 12.4157 16.6821 12 17.2344 12C17.7867 12 18.2344 12.4157 18.2344 12.9286Z" fill="#6E6E70"/>
        <path d="M13.2344 16.6429C13.2344 17.1558 12.7867 17.5715 12.2344 17.5715C11.6821 17.5715 11.2344 17.1558 11.2344 16.6429C11.2344 16.1301 11.6821 15.7144 12.2344 15.7144C12.7867 15.7144 13.2344 16.1301 13.2344 16.6429Z" fill="#6E6E70"/>
        <path d="M13.2344 12.9286C13.2344 13.4414 12.7867 13.8571 12.2344 13.8571C11.6821 13.8571 11.2344 13.4414 11.2344 12.9286C11.2344 12.4157 11.6821 12 12.2344 12C12.7867 12 13.2344 12.4157 13.2344 12.9286Z" fill="#6E6E70"/>
        <path d="M8.23438 16.6429C8.23438 17.1558 7.78665 17.5715 7.23438 17.5715C6.68209 17.5715 6.23438 17.1558 6.23438 16.6429C6.23438 16.1301 6.68209 15.7144 7.23438 15.7144C7.78665 15.7144 8.23438 16.1301 8.23438 16.6429Z" fill="#6E6E70"/>
        <path d="M8.23438 12.9286C8.23438 13.4414 7.78665 13.8571 7.23438 13.8571C6.68209 13.8571 6.23438 13.4414 6.23438 12.9286C6.23438 12.4157 6.68209 12 7.23438 12C7.78665 12 8.23438 12.4157 8.23438 12.9286Z" fill="#6E6E70"/>
      </svg>
    )
  }

  const svg_delete = () => {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="25"
        height="24"
        viewBox="0 0 25 24"
        fill="none"
      >
        <mask
          id="mask0_19_731"
          // style="mask-type:alpha"
          maskUnits="userSpaceOnUse"
          x="0"
          y="0"
          width="25"
          height="24"
        >
          <rect x="0.234375" width="24" height="24" fill="#D9D9D9" />
        </mask>
        <g mask="url(#mask0_19_731)">
          <path
            d="M11.4607 13.0004H13.4221H17.3447V11.0004H13.4221H11.4607H7.53808V13.0004H11.4607ZM12.4414 22.0004C11.0848 22.0004 9.80995 21.7377 8.61681 21.2124C7.42367 20.6877 6.3858 19.9754 5.50321 19.0754C4.62061 18.1754 3.92205 17.117 3.40753 15.9004C2.89235 14.6837 2.63477 13.3837 2.63477 12.0004C2.63477 10.617 2.89235 9.31703 3.40753 8.10037C3.92205 6.8837 4.62061 5.82537 5.50321 4.92537C6.3858 4.02537 7.42367 3.3127 8.61681 2.78737C9.80995 2.2627 11.0848 2.00037 12.4414 2.00037C13.798 2.00037 15.0728 2.2627 16.266 2.78737C17.4591 3.3127 18.497 4.02537 19.3796 4.92537C20.2622 5.82537 20.9607 6.8837 21.4753 8.10037C21.9904 9.31703 22.248 10.617 22.248 12.0004C22.248 13.3837 21.9904 14.6837 21.4753 15.9004C20.9607 17.117 20.2622 18.1754 19.3796 19.0754C18.497 19.9754 17.4591 20.6877 16.266 21.2124C15.0728 21.7377 13.798 22.0004 12.4414 22.0004ZM12.4414 20.0004C14.6315 20.0004 16.4866 19.2254 18.0067 17.6754C19.5267 16.1254 20.2867 14.2337 20.2867 12.0004C20.2867 9.76703 19.5267 7.87537 18.0067 6.32537C16.4866 4.77537 14.6315 4.00037 12.4414 4.00037C10.2512 4.00037 8.39616 4.77537 6.87613 6.32537C5.35611 7.87537 4.59609 9.76703 4.59609 12.0004C4.59609 14.2337 5.35611 16.1254 6.87613 17.6754C8.39616 19.2254 10.2512 20.0004 12.4414 20.0004Z"
            fill="#325CEB"
          />
        </g>
      </svg>
    );
  };

  const svg_add = () => {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="25"
        height="24"
        viewBox="0 0 25 24"
        fill="none"
      >
        <path
          d="M11.2339 17H13.2339V13H17.2339V11H13.2339V7H11.2339V11H7.23389V13H11.2339V17ZM12.2339 22C10.8506 22 9.55055 21.7417 8.33389 21.225C7.11722 20.6917 6.05889 19.975 5.15889 19.075C4.25889 18.175 3.54222 17.1167 3.00889 15.9C2.49222 14.6833 2.23389 13.3833 2.23389 12C2.23389 10.6167 2.49222 9.31667 3.00889 8.1C3.54222 6.88333 4.25889 5.825 5.15889 4.925C6.05889 4.025 7.11722 3.31667 8.33389 2.8C9.55055 2.26667 10.8506 2 12.2339 2C13.6172 2 14.9172 2.26667 16.1339 2.8C17.3506 3.31667 18.4089 4.025 19.3089 4.925C20.2089 5.825 20.9172 6.88333 21.4339 8.1C21.9672 9.31667 22.2339 10.6167 22.2339 12C22.2339 13.3833 21.9672 14.6833 21.4339 15.9C20.9172 17.1167 20.2089 18.175 19.3089 19.075C18.4089 19.975 17.3506 20.6917 16.1339 21.225C14.9172 21.7417 13.6172 22 12.2339 22ZM12.2339 20C14.4672 20 16.3589 19.225 17.9089 17.675C19.4589 16.125 20.2339 14.2333 20.2339 12C20.2339 9.76667 19.4589 7.875 17.9089 6.325C16.3589 4.775 14.4672 4 12.2339 4C10.0006 4 8.10889 4.775 6.55889 6.325C5.00889 7.875 4.23389 9.76667 4.23389 12C4.23389 14.2333 5.00889 16.125 6.55889 17.675C8.10889 19.225 10.0006 20 12.2339 20Z"
          fill="#325CEB"
        />
      </svg>
    );
  };

  // 创建带 Popover 的 Radio 组件
  const RadioWithPopover = ({ value, children, style, ...props }: any) => {
    return (
      <Radio 
        value={value} 
        style={{ 
          ...style, 
          whiteSpace: "normal",
          wordBreak: "break-word",
          lineHeight: "1.4"
        }} 
        {...props}
      >
        {children}
      </Radio>
    );
  };

  const L1 = () => {
    return (
      <div className="L1" id="L1">
        <div className="L-title">L1. Property and Loan Information</div>
        <>
          <div style={{ marginBottom: "28px" }}>
            <div className="title-gray" style={{ marginBottom: "10px" }}>
              Community Property State
            </div>
            <Checkbox.Group
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                textAlign: "left",
              }}
              className={getDisabledClassName("checkboxgroup", !editMode)}
              value={communityPropertyState}
              onChange={(e) => setCommunityPropertyState(e as string[])}
              disabled={!editMode}
            >
              <Checkbox value="borrower" className="checkbox-label-black">
                At least one borrower lives in a community property state
              </Checkbox>
              <Checkbox value="property" className="checkbox-label-black">
                Property is in a community property state
              </Checkbox>
            </Checkbox.Group>
          </div>
        </>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "20px",
            marginBottom: "28px",
          }}
        >
          <div style={{ flex: 1, maxWidth: "calc(50% - 10px)" }}>
            <div className="title-gray">Transaction Detail</div>
            <div style={{ display: "flex", gap: 20, flex: 1 }}>
              <Select
                style={{ width: "100%", height: "48px" }}
                className={getDisabledClassName("select", !editMode)}
                placeholder="Select Transaction Detail"
                value={transactionDetail}
                onChange={setTransactionDetail}
                disabled={!editMode}
                suffixIcon={dropdown_svg()}
              >
                <Select.Option value="conversion">
                  Conversion of Contract for Deed or Land Contract
                </Select.Option>
                <Select.Option value="renovation">Renovation</Select.Option>
                <Select.Option value="construction">
                  Construction-Conversion/Construction-to-Permanent
                </Select.Option>
              </Select>
            </div>
          </div>
          {transactionDetail === "construction" && (
            <div style={{ flex: 1 }}>
              <div className="title-gray">Closing type</div>
              <Select
                placeholder="Closing type"
                style={{ width: "100%", height: "48px" }}
                className={getDisabledClassName("select", !editMode)}
                value={closingType}
                onChange={setClosingType}
                disabled={!editMode}
                suffixIcon={dropdown_svg()}
              >
                <Select.Option value="single-closing">
                  Single-Closing
                </Select.Option>
                <Select.Option value="two-closing">Two-Closing</Select.Option>
              </Select>
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 20, marginBottom: "28px" }}>
          <div style={{ flex: 1 }}>
            <div className="title-gray">Construction/Improvement Cost</div>
            <DollarInputNumber
              style={{ width: "100%", height: "48px", borderRadius: "8px"  }}
              className={`input-number ${getDisabledClassName(
                "dollarinputnumber",
                !editMode
              )}`}
              min={0}
              max={99999999.99}
              value={constructionCost}
              onChange={setConstructionCost}
              disabled={!editMode}
            />
          </div>
          <div style={{ flex: 1 }}>
            <div className="title-gray">Lot Acquired Date</div>
            <DatePicker
              placeholder="MM/DD/YYYY"
              style={{ width: "100%", height: "48px" }}
              className={getDisabledClassName("datepicker", !editMode)}
              value={lotAcquiredDate ? dayjs(lotAcquiredDate, "MM/DD/YYYY") : null}
              onChange={(date, dateString) => setLotAcquiredDate(dateString)}
              disabled={!editMode}
              suffixIcon={date_svg()}
              format={{
                format: "MM/DD/YYYY",
                type: 'mask',
              }}
            />
          </div>
        </div>
        <>
          <div style={{ marginBottom: "0px" }}>
            <div className="title-gray">Original Cost of Lot</div>
            <DollarInputNumber
              style={{ width: "calc(50% - 10px)", height: "48px" }}
              className={`input-number ${getDisabledClassName(
                "dollarinputnumber",
                !editMode
              )}`}
              min={0}
              max={99999999.99}
              value={originalLotCost}
              onChange={setOriginalLotCost}
              disabled={!editMode}
            />
          </div>
        </>
        <Line></Line>
        <Checkbox
          checked={isRefinance}
          style={{ marginBottom: "28px" }}
          className={getDisabledClassName("checkbox", !editMode)}
          onChange={(e) => setIsRefinance(e.target.checked)}
          disabled={!editMode}
        >
          <span className="checkbox-label">This is a refinance loan</span>
        </Checkbox>
        {isRefinance && (
          <>
            <div style={{ marginBottom: "28px" }}>
              <div className="title-gray">Refinance Type</div>
              <Radio.Group
                className={`radio-group-bg ${getDisabledClassName(
                  "radiogroup",
                  !editMode
                )}`}
                style={{ display: "flex", flexDirection: "row", gap: "20px" }}
                value={refinanceType}
                onChange={(e) => setRefinanceType(e.target.value)}
                disabled={!editMode}
              >
                <Radio value="no-cash-out">No cash out</Radio>
                <Radio value="limited-cash-out">Limited cash out</Radio>
                <Radio value="cash-out">Cash out</Radio>
              </Radio.Group>
            </div>
            <div style={{ marginBottom: "28px" }}>
              <div className="title-gray">Refinance Program</div>
              <Radio.Group
                value={refinanceProgram}
                onChange={(e) => setRefinanceProgram(e.target.value)}
                className={`radio-group-bg ${getDisabledClassName(
                  "radiogroup",
                  !editMode
                )}`}
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "20px",
                  flexWrap: "wrap",
                  width: "100%",
                }}
                disabled={!editMode}
              >
                <RadioWithPopover
                  value="full-documentation"
                  style={{
                    width: "calc(33% - 10px)",
                    minWidth: "calc(32% - 10px)",
                  }}
                >
                  Full Documentation
                </RadioWithPopover>
                <RadioWithPopover
                  value="interest-rate-reduction"
                  style={{
                    width: "calc(33% - 10px)",
                    minWidth: "calc(32% - 10px)",
                  }}
                >
                  Interest Rate Reduction
                </RadioWithPopover>
                <RadioWithPopover
                  value="streamlined-without-appraisal"
                  style={{
                    width: "calc(33% - 10px)",
                    minWidth: "calc(32% - 10px)",
                  }}
                >
                  Streamlined without Appraisal
                </RadioWithPopover>
                <Radio
                  value="other"
                  style={{
                    width: "calc(33% - 10px)",
                    minWidth: "calc(32% - 10px)",
                    maxWidth: "calc(32% - 10px)",
                  }}
                >
                  Other
                </Radio>
                {refinanceProgram === "other" && (
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "row",
                      gap: "10px",
                      alignItems: "center",
                      height: "48px",
                    }}
                  >
                    <span className="ohter-explain-label">Other(explain):</span>
                    <Input
                      style={{
                        width: "100%",
                        height: "48px",
                        borderRadius: "8px",
                        textAlign: "left",
                      }}
                      className={`w-100 ${getDisabledClassName(
                        "input",
                        !editMode
                      )}`}
                      placeholder="Please explain"
                      value={otherRefinanceProgramExplain}
                      onChange={(e) =>
                        setOtherRefinanceProgramExplain(e.target.value)
                      }
                      disabled={!editMode}
                    />
                  </div>
                )}
              </Radio.Group>
            </div>
          </>
        )}
        <>
          <div className="title-gray" style={{ marginBottom: "10px" }}>
            Energy Improvement
          </div>
          <Checkbox.Group
            className={`checkbox-group-bg ${getDisabledClassName(
              "checkboxgroup",
              !editMode
            )}`}
            value={energyImprovements}
            onChange={(e) => setEnergyImprovements(e as string[])}
            disabled={!editMode}
          >
            <Checkbox value="energy-improvements">
              <span className="checkbox-label-black-energy">
                Mortgage loan will finance energy-related improvements
              </span>
            </Checkbox>
            <Checkbox 
              style={{ marginTop: "8px" }}
              value="clean-energy-lien" 
              className="checkbox-top-align"
            >
              <span className="checkbox-label-black-energy">
                Property is currently subject to a lien that could take priority
                over the first mortgage lien, such as a clean energy lien paid
                for through property taxes (e.g., the Property Assessed Clean
                Energy program).
              </span>
            </Checkbox>
          </Checkbox.Group>
        </>
        <Line></Line>
        <div style={{ marginBottom: "28px" }}>
          <div className="title-gray">Project Type</div>
          <Radio.Group
            value={projectType}
            onChange={(e) => setProjectType(e.target.value)}
            className={`radio-group-bg ${getDisabledClassName(
              "radiogroup",
              !editMode
            )}`}
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "20px",
              flexWrap: "wrap",
              width: "100%",
            }}
            disabled={!editMode}
          >
            <Radio
              value="Condominium"
              style={{
                width: "calc(50% - 10px)",
                minWidth: "calc(50% - 10px)",
              }}
            >
              Condominium
            </Radio>
            <Radio
              value="Cooperative"
              style={{
                width: "calc(50% - 10px)",
                minWidth: "calc(50% - 10px)",
              }}
            >
              Cooperative
            </Radio>
            <RadioWithPopover
              value="planned-unit-development"
              style={{
                width: "calc(50% - 10px)",
                minWidth: "calc(50% - 10px)",
              }}
            >
              Planned unit development (PUD)
            </RadioWithPopover>
            <RadioWithPopover
              value="property-not-located-in-a-project"
              style={{
                width: "calc(50% - 10px)",
                minWidth: "calc(50% - 10px)",
              }}
            >
              Property is not located in a project
            </RadioWithPopover>
          </Radio.Group>
        </div>
      </div>
    );
  };

  const L2 = () => {
    return (
      <div className="L2" id="L2">
        <div className="L-title">L2. Title Information</div>
        <div className="title-gray">
          <div style={{ marginBottom: "9px" }}>
            Title will be held in what name(s)
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "12px 20px",
              flexWrap: "wrap",
              marginBottom: "12px",
            }}
          >
            {names.map((name, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  alignItems: "center",
                  width: "calc(50% - 10px)",
                }}
              >
                <Input
                  style={{ width: "100%", height: "48px", marginRight: "12px" }}
                  key={index}
                  value={name}
                  onChange={(e) => updateName(index, e.target.value)}
                  className={`input-number ${getDisabledClassName(
                    "input",
                    !editMode
                  )}`}
                  disabled={!editMode}
                />
                {editMode && (
                  <span
                    onClick={() =>
                      setNames(names.filter((_, i) => i !== index))
                    }
                  >
                    {svg_delete()}
                  </span>
                )}
              </div>
            ))}
          </div>
          {editMode && (
            <div style={{ marginBottom: "28px", display: "flex", flexDirection: "row",alignItems: "center", cursor: "pointer" }} onClick={() => setNames([...names, ""])}>
              {svg_add()}
              <span
                className="add-name"
                
              >
                Add name(s)
              </span>
            </div>
          )}
          <div
            style={{
              marginBottom: "28px",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            <span className="title-gray">Estate will be held in</span>
            <Radio.Group
              className={`radio-group-bg ${getDisabledClassName(
                "radiogroup",
                !editMode
              )}`}
              style={{
                display: "flex",
                flexDirection: "row",
                gap: "20px",
                flexWrap: "wrap",
                width: "100%",
              }}
              value={estateType}
              onChange={(e) => setEstateType(e.target.value)}
              disabled={!editMode}
            >
              <Radio value="fee-simple" style={{ flex: 1 }}>
                Fee simple
              </Radio>
              <Radio value="leasehold" style={{ flex: 1 }}>
                Leasehold
              </Radio>
            </Radio.Group>
          </div>
          {estateType === "leasehold" && (
            <div style={{ marginBottom: "28px" }}>
              <div className="title-gray">Expiration Date</div>
              <DatePicker
                placeholder="MM/DD/YYYY"
                style={{ width: "calc(50% - 10px)", height: "48px" }}
                className={getDisabledClassName("datepicker", !editMode)}
                value={leaseholdExpirationDate ? dayjs(leaseholdExpirationDate, "MM/DD/YYYY") : null}
                onChange={(date, dateString) => setLeaseholdExpirationDate(dateString)}
                disabled={!editMode}
                suffixIcon={date_svg()}
                format={{
                  format: "MM/DD/YYYY",
                  type: 'mask',
                }}
              />
            </div>
          )}
          <div>
            <div className="title-gray">Manner in which title will be held</div>
            <Select
              style={{ width: "calc(50% - 10px)", height: "48px" }}
              className={getDisabledClassName("select", !editMode)}
              value={titleManner}
              onChange={setTitleManner}
              disabled={!editMode}
              suffixIcon={dropdown_svg()}
            >
              <Select.Option value="sole-ownership">
                Sole Ownership
              </Select.Option>
              <Select.Option value="life-estate">Life Estate</Select.Option>
              <Select.Option value="tenancy-in-common">
                Tenancy in Common
              </Select.Option>
              <Select.Option value="joint-tenancy">
                Joint Tenancy with Right of Survivorship
              </Select.Option>
              <Select.Option value="tenancy-entirety">
                Tenancy by the Entirety{" "}
              </Select.Option>
              <Select.Option value="other">Other</Select.Option>
            </Select>
          </div>
          <Line></Line>
          <Checkbox
            style={{ marginBottom: !isRefinanceLoan_L2 ? "0px" : "28px" }}
            className={getDisabledClassName("checkbox", !editMode)}
            checked={isRefinanceLoan_L2}
            onChange={(e) => setIsRefinanceLoan_L2(e.target.checked)}
            disabled={!editMode}
          >
            <span className="checkbox-label-black">
              This is a refinance loan
            </span>
          </Checkbox>
          {isRefinanceLoan_L2 && (
            <div>
              <div>
                <div className="title-gray">
                  For refinance: Title is currently held in what name(s)
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "12px 20px",
                    flexWrap: "wrap",
                    width: "100%",
                  }}
                >
                  {names_refinance.map((name, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "12px",
                        width: "calc(50% - 10px)",
                      }}
                    >
                      <Input
                        style={{
                          width: "100%",
                          height: "48px",
                          marginRight: "12px",
                        }}
                        key={index}
                        value={name}
                        onChange={(e) =>
                          updateName(index, e.target.value, true)
                        }
                        className={`input-number ${getDisabledClassName(
                          "input",
                          !editMode
                        )}`}
                        disabled={!editMode}
                      />
                      {editMode && (
                        <span
                          onClick={() =>
                            setNames_refinance(
                              names_refinance.filter((_, i) => i !== index)
                            )
                          }
                        >
                          {svg_delete()}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              {editMode && (
                <div
                  onClick={() => setNames_refinance([...names_refinance, ""])}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "10px",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                >
                  <span>{svg_add()}</span>{" "}
                  <span className="add-name">Add name(s)</span>
                </div>
              )}
            </div>
          )}
          <Line></Line>
          <div>
            <div className="title-gray">Trust Information</div>
            <Radio.Group
              className={`radio-group-bg ${getDisabledClassName(
                "radiogroup",
                !editMode
              )}`}
              style={{ width: "100%" }}
              value={trustInformation}
              onChange={(e) => setTrustInformation(e.target.value)}
              disabled={!editMode}
            >
              <Radio value="inter-vivos" style={{ width: "100%", fontWeight: "400" }}>
                <span className="checkbox-label-black">
                Title will be held by an inter vivos (living) trust 
                </span>
              </Radio>
              <Radio value="land-trust" style={{ width: "100%", fontWeight: "400" }}>
                <span className="checkbox-label-black">
                  Title will be held by a land trust{" "}
                </span>
              </Radio>
            </Radio.Group>
          </div>
          <div style={{ marginBottom: "28px" }}>
            <div className="title-gray">Indian county land tenure</div>
            <Select
              style={{ width: "calc(50% - 10px)", height: "48px" }}
              className={getDisabledClassName("select", !editMode)}
              value={indianLandTenure}
              onChange={setIndianLandTenure}
              disabled={!editMode}
              suffixIcon={dropdown_svg()}
            >
              <Select.Option value="fee-simple-reservation">
                Fee Simple On a Reservation
              </Select.Option>
              <Select.Option value="individual-trust-land">
                Individual Trust Land (Allotted/Restricted)
              </Select.Option>
              <Select.Option value="tribal-trust-reservation">
                Tribal Trust Land On a Reservation
              </Select.Option>
              <Select.Option value="tribal-trust-off-reservation">
                Tribal Trust Land Off Reservation
              </Select.Option>
              <Select.Option value="alaska-native-corporation">
                Alaska Native Corporation Land
              </Select.Option>
            </Select>
          </div>
        </div>
        <div></div>
        <div></div>
      </div>
    );
  };

  const L3 = () => {
    return (
      <div className="L3" id="L3">
        <div className="L-title">L3. Mortgage Loan Information</div>
        <div className="L3-row-item-between">
          <div className="item" style={{width: "calc(50% - 10px)"}}>
            <div className="label">Mortgage type applied for</div>
            <Select
              className={`select ${getDisabledClassName("select", !editMode)}`}
              value={mortgageType}
              onChange={(value) => setMortgageType(value as string)}
              disabled={!editMode}
              suffixIcon={dropdown_svg()}
            >
              <Select.Option value="Conventional">Conventional</Select.Option>
              <Select.Option value="USDA-RD">USDA-RD</Select.Option>
              <Select.Option value="FHA">FHA</Select.Option>
              <Select.Option value="VA">VA</Select.Option>
              <Select.Option value="other">Other</Select.Option>
            </Select>
          </div>
          {mortgageType === "other" && (
            <div className="item" style={{width: "calc(50% - 10px)"}}>
              <div className="label">Other mortgage type</div>
                <Input
                  style={{width: "100%", textAlign: "left"}}
                  className={`input-number ${getDisabledClassName(
                    "input",
                    !editMode
                  )}`}
                  value={otherMortgageType}
                  onChange={(e) => setOtherMortgageType(e.target.value)}
                  disabled={!editMode}
                  placeholder="Other mortgage type"
                />
            </div>
          )}
        </div>
        <Line></Line>

        <div className="L3-row-item-between">
          <div className="item" style={{ marginBottom: "28px", width: "calc(50% - 10px)" }}>
            <div className="label">Amortization type</div>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                gap: "20px",
                alignItems: "flex-start",
                width: "100%",
                flexWrap: "wrap"
              }}
            >
              <Select
                style={{width: "100%"}}
                value={amortizationType}
                onChange={(value) => setAmortizationType(value as string)}
                className={`select ${getDisabledClassName("select", !editMode)}`}
                disabled={!editMode}
                suffixIcon={dropdown_svg()}
              >
                <Select.Option value="fixed-rate">Fixed rate</Select.Option>
                <Select.Option value="adjustable-rate">
                  Adjustable Rate
                </Select.Option>
                <Select.Option value="other">Other</Select.Option>
              </Select>
            </div>
          </div>
          {amortizationType === "other" && <div className="item" style={{ width: "calc(50% - 10px)" }}>
              <div className="label" style={{ whiteSpace: "nowrap", fontSize: "14px", color: "#666" }}>
                Other (explain)
              </div>
              {/* <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "12px",
                  alignItems: "center",
                  width: "calc(50% - 10px)",
                  minWidth: "300px"
                }}
              > */}
                <Input
                  style={{ height: "48px", textAlign: "left", border: "1px solid #ECECEC" }}
                  className={`input-number ${getDisabledClassName(
                    "input",
                    !editMode
                  )}`}
                  value={otherAmortizationExplain}
                  onChange={(e) => setOtherAmortizationExplain(e.target.value)}
                  disabled={!editMode}
                  placeholder="Other amortization type"
                />
              {/* </div> */}
          </div>}
        </div>
        {amortizationType === "adjustable-rate" && (
          <div
            className="L3-row-item-between"
            style={{
              marginBottom: "28px",
              width: "100%",
              display: "flex",
              flexDirection: "row",
              gap: "20px",
            }}
          >
            <div className="item" style={{ flex: 1 }}>
              <div className="label">Initial Period Prior to First Adjustment</div>
              <InputNumber
                style={{ width: "100%" }}
                suffix="months"
                min={0}
                max={100}
                className={`input-number select ${getDisabledClassName(
                  "inputnumber",
                  !editMode
                )}`}
                value={interestOnlyTerm}
                onChange={(value) => setInterestOnlyTerm(value as number)}
                disabled={!editMode}
              />
            </div>
            <div className="item" style={{ flex: 1 }}>
              <div className="label">Subsequent Adjustment Period </div>
              <InputNumber
                style={{ width: "100%" }}
                suffix="months"
                min={0}
                max={100}
                className={`input-number select ${getDisabledClassName(
                  "inputnumber",
                  !editMode
                )}`}
                value={subsequentAdjustmentPeriod}
                onChange={(value) =>
                  setSubsequentAdjustmentPeriod(value as number)
                }
                disabled={!editMode}
              />
            </div>
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <div className="label" style={{ marginBottom: "8px" }}>
            Loan features
          </div>
          <Checkbox.Group
            value={loanFeatures}
            onChange={(e) => setLoanFeatures(e as string[])}
            className={getDisabledClassName("checkboxgroup", !editMode)}
            disabled={!editMode}
          >
            <Checkbox value="balloon" style={{ width: "100%" }}>
              <div 
                className="item-row"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <span className="checkbox-label">Balloon/ Balloon term</span>
                <div
                  onMouseDown={(e) => {
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                  }}
                  onKeyUp={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <div
                    onMouseDown={(e) => {
                      // e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onKeyDown={(e) => {
                      // e.preventDefault();
                      e.stopPropagation();
                    }}
                    onKeyUp={(e) => {
                      // e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    <InputNumber
                      suffix="months"
                      min={0}
                      max={600}
                      className={`input-number ${getDisabledClassName(
                        "inputnumber",
                        !editMode
                      )}`}
                      value={balloonTerm}
                      onChange={(value) => {
                        setBalloonTerm(value as number);
                      }}
                      disabled={!editMode}
                    />
                  </div>
                </div>
              </div>
            </Checkbox>
            <Checkbox value="interest-only" style={{ width: "100%" }}>
              <div 
                className="item-row"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <span className="checkbox-label">
                  Interest Only / Interest Only Term
                </span>
                <div
                  onMouseDown={(e) => {
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                  }}
                  onKeyUp={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <div
                    onMouseDown={(e) => {
                      // e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onKeyDown={(e) => {
                      // e.preventDefault();
                      e.stopPropagation();
                    }}
                    onKeyUp={(e) => {
                      // e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    <InputNumber
                      suffix="months"
                      min={0}
                      max={600}
                      className={`input-number ${getDisabledClassName(
                        "inputnumber",
                        !editMode
                      )}`}
                      value={interestOnlyTermFeature}
                      onChange={(value) => {
                        setInterestOnlyTermFeature(value as number);
                      }}
                      disabled={!editMode}
                    />
                  </div>
                </div>
              </div>
            </Checkbox>
            <Checkbox value="negative-amortization" style={{ width: "100%" }}>
              <div className="item-row">
                <span className="checkbox-label">Negative Amortization</span>
                {/* <InputNumber
                  suffix="months"
                  min={0}
                  max={100}
                  className="input-number"
                  value={negativeAmortizationTerm}
                  onChange={(value) =>
                    setNegativeAmortizationTerm(value as number)
                  }
                /> */}
              </div>
            </Checkbox>
            <Checkbox value="prepayment-penalty" style={{ width: "100%" }}>
              <div 
                className="item-row"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <span className="checkbox-label">
                  Prepayment Penalty / Prepayment Penalty Term
                </span>
                <div
                  onMouseDown={(e) => {
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                  }}
                  onKeyUp={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <div
                    onMouseDown={(e) => {
                      // e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onKeyDown={(e) => {
                      // e.preventDefault();
                      e.stopPropagation();
                    }}
                    onKeyUp={(e) => {
                      // e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    <InputNumber
                      suffix="months"
                      min={0}
                      max={600}
                      className={`input-number ${getDisabledClassName(
                        "inputnumber",
                        !editMode
                      )}`}
                      value={prepaymentPenaltyTerm}
                      onChange={(value) => {
                        setPrepaymentPenaltyTerm(value as number);
                      }}
                      disabled={!editMode}
                    />
                  </div>
                </div>
              </div>
            </Checkbox>
            <Checkbox value="buydown" style={{ width: "100%" }}>
              <div 
                className="item-row"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <span className="checkbox-label">
                  Temporary Interest Rate Buydown / Initial Buydown Rate
                </span>
                <div
                  onMouseDown={(e) => {
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                  }}
                  onKeyUp={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <div
                    onMouseDown={(e) => {
                      // e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onKeyDown={(e) => {
                      // e.preventDefault();
                      e.stopPropagation();
                    }}
                    onKeyUp={(e) => {
                      // e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    <InputNumber
                      suffix="%"
                      min={0}
                      max={1000}
                      className={`input-number ${getDisabledClassName(
                        "inputnumber",
                        !editMode
                      )}`}
                      value={buydownRate}
                      onChange={(value) => {
                        setBuydownRate(value as number);
                      }}
                      disabled={!editMode}
                    />
                  </div>
                </div>
              </div>
            </Checkbox>
            <Checkbox value="other-feature" style={{ width: "100%" }}>
              <div 
                className="item-row"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  className="checkbox-label"
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "8px",
                    width: "100%",
                    alignItems: "center",
                  }}
                >
                  <span>Other (explain):</span>
                  {/* <div
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onKeyDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onKeyUp={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  > */}
                    <Input
                      className={`input-number ${getDisabledClassName(
                        "input",
                        !editMode
                      )}`}
                      style={{ flex: 1, textAlign: "left" }} // width: 308
                      value={otherLoanFeature}
                      onChange={(e) => {
                        setOtherLoanFeature(e.target.value);
                      }}
                      disabled={!editMode}
                    />
                  {/* </div> */}
                </div>
                {/* <InputNumber
                  suffix="months"
                  min={0}
                  max={100}
                  className="input-number"
                  value={otherLoanFeatureTerm}
                  onChange={(value) => setOtherLoanFeatureTerm(value as number)}
                /> */}
              </div>
            </Checkbox>
          </Checkbox.Group>
        </div>
        <Line></Line>
        <div
          className=""
          style={{ display: "flex", flexDirection: "column", gap: "28px" }}
        >
          <div className="title-gray">Terms of loan</div>
          <div className="L3-row-item" style={{ marginBottom: "0px" }}>
            <div className="item" style={{ flex: 1 }}>
              <div className="label">Note rate (%)</div>
              <InputNumber
                suffix="%"
                min={0}
                max={1000}
                className={`input-number-3 ${getDisabledClassName(
                  "inputnumber",
                  !editMode
                )}`}
                value={noteRate}
                onChange={(value) => setNoteRate(value as number)}
                disabled={!editMode}
              />
            </div>
            <div className="item" style={{ flex: 1 }}>
              <div className="label">Loan term (months)</div>
              <InputNumber
                suffix="months"
                min={0}
                max={600}
                className={`input-number-3 ${getDisabledClassName(
                  "inputnumber",
                  !editMode
                )}`}
                value={loanTerm}
                onChange={(value) => setLoanTerm(value as number)}
                disabled={!editMode}
              />
            </div>
          </div>
        </div>
        <div className="item" style={{ flex: 1, width: "calc(50% - 10px)", marginTop: "28px" }}>
          <div className="label">Mortgage lien type</div>
          <Select
            className={`select-3 ${getDisabledClassName(
              "select",
              !editMode
            )}`}
            value={mortgageLienType}
            onChange={setMortgageLienType}
            disabled={!editMode}
            suffixIcon={dropdown_svg()}
          >
            <Select.Option value="first-lien">First lien</Select.Option>
            <Select.Option value="subordinate-lien">
              Subordinate lien
            </Select.Option>
          </Select>
        </div>
        <Line></Line>
        <div className="title-gray" style={{ marginBottom: "28px" }}>
          Proposed Monthly Payment for Property
        </div>
        <div className="L3-row-item">
          <div className="item" style={{ flex: 1 }}>
            <div className="label">First Mortgage (P & I)</div>
            <DollarInputNumber
              style={{ width: "100%" }}
              min={0}
              max={99999999.99}
              className={`input-number ${getDisabledClassName(
                "dollarinputnumber",
                !editMode
              )}`}
              value={firstMortgagePayment}
              onChange={setFirstMortgagePayment}
              disabled={!editMode}
            />
          </div>
          <div className="item" style={{ flex: 1 }}>
            <div className="label">Subordinate Lien(s) (P & I)</div>
            <DollarInputNumber
              style={{ width: "100%" }}
              min={0}
              max={99999999.99}
              className={`input-number ${getDisabledClassName(
                "dollarinputnumber",
                !editMode
              )}`}
              value={subordinateLienPayment}
              onChange={setSubordinateLienPayment}
              disabled={!editMode}
            />
          </div>
        </div>
        <div className="L3-row-item">
          <div className="item" style={{ flex: 1 }}>
            <div className="label">Homeowner's Insurance</div>
            <DollarInputNumber
              style={{ width: "100%" }}
              min={0}
              max={99999999.99}
              className={`input-number ${getDisabledClassName(
                "dollarinputnumber",
                !editMode
              )}`}
              value={homeownersInsurance}
              onChange={setHomeownersInsurance}
              disabled={!editMode}
            />
          </div>
          <div className="item" style={{ flex: 1 }}>
            <div className="label">Supplemental Property Insurance</div>
            <DollarInputNumber
              style={{ width: "100%" }}
              min={0}
              max={99999999.99}
              className={`input-number ${getDisabledClassName(
                "dollarinputnumber",
                !editMode
              )}`}
              value={supplementalInsurance}
              onChange={setSupplementalInsurance}
              disabled={!editMode}
            />
          </div>
        </div>

        <div className="L3-row-item">
          <div className="item" style={{ flex: 1 }}>
            <div className="label">Property Taxes</div>
            <DollarInputNumber
              style={{ width: "100%" }}
              min={0}
              max={99999999.99}
              className={`input-number ${getDisabledClassName(
                "dollarinputnumber",
                !editMode
              )}`}
              value={propertyTaxes}
              onChange={setPropertyTaxes}
              disabled={!editMode}
            />
          </div>
          <div className="item" style={{ flex: 1 }}>
            <div className="label">Mortgage Insurance</div>
            <DollarInputNumber
              style={{ width: "100%" }}
              min={0}
              max={99999999.99}
              className={`input-number ${getDisabledClassName(
                "dollarinputnumber",
                !editMode
              )}`}
              value={mortgageInsurance}
              onChange={setMortgageInsurance}
              disabled={!editMode}
            />
          </div>
        </div>

        <div className="L3-row-item">
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              flex: 1,
              // width: "100%",
              marginBottom: "24px",
            }}
          >
            <div className="label">
              Association/Project Dues (Condo, Co-Op, PUD)
            </div>
            <DollarInputNumber
              style={{ width: "100%" }}
              min={0}
              max={99999999.99}
              className={`input-number ${getDisabledClassName(
                "dollarinputnumber",
                !editMode
              )}`}
              value={associationDues}
              onChange={setAssociationDues}
              disabled={!editMode}
            />
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              flex: 1,
              // width: "100%",
              marginBottom: "24px",
            }}
          >
            <div className="label">Other</div>
            <DollarInputNumber
              style={{ width: "100%" }}
              min={0}
              max={99999999.99}
              className={`input-number ${getDisabledClassName(
                "dollarinputnumber",
                !editMode
              )}`}
              value={otherMonthlyExpenses}
              onChange={setOtherMonthlyExpenses}
              disabled={!editMode}
            />
          </div>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            width: "100%",
            marginBottom: "28px",
          }}
        >
          <div className="label">Total monthly payment</div>
          <DollarInputNumber
            style={{ width: "calc(50% - 10px)" }}
            min={0}
            max={99999999.99}
            className={`input-number ${getDisabledClassName(
              "dollarinputnumber",
              true
            )}`}
            value={totalMonthlyPayment}
            // onChange={setTotalMonthlyPayment}
            disabled={true}
          />
        </div>
      </div>
    );
  };
  const L4 = () => {
    return (
      <div className="L4" id="L4">
        <div className="L-title">
          L4. Qualifying the Borrower - Minimum Required Funds or Cash Back
        </div>
        <div className="L4-content-section">
          <div className="title">DUE FROM BORROWER(S)</div>
          <div className="row-item">
            <div className="label">A. Sales Contract Price</div>
            <DollarInputNumber
              className={`input-number ${getDisabledClassName(
                "dollarinputnumber",
                !editMode
              )}`}
              min={0}
              max={99999999.99}
              value={salesContractPrice}
              onChange={setSalesContractPrice}
              disabled={!editMode}
            />
          </div>
          <div className="row-item">
            <div className="label">
              B. Improvements, Renovations, and Repairs
            </div>
            <DollarInputNumber
              className={`input-number ${getDisabledClassName(
                "dollarinputnumber",
                !editMode
              )}`}
              min={0}
              max={99999999.99}
              value={improvementsCost}
              onChange={setImprovementsCost}
              disabled={!editMode}
            />
          </div>
          <div className="row-item">
            <div className="label">C. Land (if acquired separately) </div>
            <DollarInputNumber
              className={`input-number ${getDisabledClassName(
                "dollarinputnumber",
                !editMode
              )}`}
              min={0}
              max={99999999.99}
              value={landCost}
              onChange={setLandCost}
              disabled={!editMode}
            />
          </div>
          <div className="row-item">
            <div className="label">
              D. For Refinance: Balance of Mortgage Loans on the Property to be
              paid off in the Transaction{" "}
              <span style={{ fontStyle: "italic", fontWeight: 400 }}>
                (See Table 3a. Property You Own)
              </span>
            </div>
            <DollarInputNumber
              className={`input-number ${getDisabledClassName(
                "dollarinputnumber",
                !editMode
              )}`}
              min={0}
              max={99999999.99}
              value={refinanceBalance}
              onChange={setRefinanceBalance}
              disabled={!editMode}
            />
          </div>
          <div className="row-item">
            <div className="label">
              E. Credit Cards and Other Debts Paid Off{" "}
              <span style={{ fontStyle: "italic", fontWeight: 400 }}>
                (See Table 2c. Liabilities - Credit Cards, Other Debts, and
                Leases that You Owe)
              </span>
            </div>
            <DollarInputNumber
              className={`input-number ${getDisabledClassName(
                "dollarinputnumber",
                !editMode
              )}`}
              min={0}
              max={99999999.99}
              value={creditCardsDebts}
              onChange={setCreditCardsDebts}
              disabled={!editMode}
            />
          </div>{" "}
          <div className="row-item">
            <div className="label">
              F. Borrower Closing Costs (including Prepaid and Initial Escrow
              Payments)
            </div>
            <DollarInputNumber
              className={`input-number ${getDisabledClassName(
                "dollarinputnumber",
                !editMode
              )}`}
              min={0}
              max={99999999.99}
              value={borrowerClosingCosts}
              onChange={setBorrowerClosingCosts}
              disabled={!editMode}
            />
          </div>
          <div className="row-item">
            <div className="label">G. Discount Points</div>
            <DollarInputNumber
              className={`input-number ${getDisabledClassName(
                "dollarinputnumber",
                !editMode
              )}`}
              min={0}
              max={99999999.99}
              value={discountPoints}
              onChange={setDiscountPoints}
              disabled={!editMode}
            />
          </div>
          <div className="row-item">
            <div className="label total">
              H. TOTAL DUE FROM BORROWER(s) (Total of A thru G){" "}
            </div>
            <DollarInputNumber
              className={`input-number ${getDisabledClassName(
                "dollarinputnumber",
                true
              )}`}
              min={0}
              max={99999999.99}
              value={totalDueFromBorrower}
              disabled={true}
            />
          </div>
        </div>
        <Line></Line>
        <div className="L4-content-section">
          <div className="title">TOTAL MORTGAGE LOANS</div>
          <div
            className="row-item"
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            <div className="label">I. Loan Amount</div>
            <div className="row-item">
              <span className="label label-gray">
                Loan Amount Excluding Financed Mortgage Insurance (or Mortgage
                Insurance Equivalent)
              </span>
              <DollarInputNumber
                className={`input-number ${getDisabledClassName(
                  "dollarinputnumber",
                  !editMode
                )}`}
                min={0}
                max={99999999.99}
                value={loanAmountExcludingInsurance}
                onChange={setLoanAmountExcludingInsurance}
                disabled={!editMode}
              />
            </div>
            <div className="row-item">
              <span className="label label-gray">
                Financed Mortgage Insurance (or Mortgage Insurance Equivalent)
                Amount
              </span>
              <DollarInputNumber
                className={`input-number ${getDisabledClassName(
                  "dollarinputnumber",
                  !editMode
                )}`}
                min={0}
                max={99999999.99}
                value={financedInsuranceAmount}
                onChange={setFinancedInsuranceAmount}
                disabled={!editMode}
              />
            </div>
          </div>
          <div className="row-item">
            <div className="label">
              J. Other New Mortgage Loans on the Property the Borrower(s) is
              Buying or Refinancing{" "}
              <span style={{ fontStyle: "italic", fontWeight: 400 }}>
                (See Table 4b. Other New Mortgage Loans on the Property You are
                Buying or Refinancing)
              </span>
            </div>
            <DollarInputNumber
              className={`input-number ${getDisabledClassName(
                "dollarinputnumber",
                !editMode
              )}`}
              min={0}
              max={99999999.99}
              value={otherNewMortgageLoans}
              onChange={setOtherNewMortgageLoans}
              disabled={!editMode}
            />
          </div>
          <div className="row-item">
            <div className="label total">
              K. TOTAL MORTGAGE LOANS (Total of I and J){" "}
            </div>
            <DollarInputNumber
              className={`input-number ${getDisabledClassName(
                "dollarinputnumber",
                true
              )}`}
              min={0}
              max={99999999.99}
              value={totalMortgageLoans}
              disabled={true}
            />
          </div>
        </div>
        <Line></Line>
        <div className="L4-content-section">
          <div className="title">TOTAL CREDITS</div>
          <div className="row-item">
            <div className="label">
              L. Seller Credits{" "}
              <span style={{ fontStyle: "italic", fontWeight: 400 }}>
                (Enter the amount of Borrower(s) costs paid by the property
                seller)
              </span>
            </div>
            <DollarInputNumber
              className={`input-number ${getDisabledClassName(
                "dollarinputnumber",
                !editMode
              )}`}
              min={0}
              max={99999999.99}
              value={sellerCredits}
              onChange={setSellerCredits}
              disabled={!editMode}
            />
          </div>
          <div className="row-item">
            <div className="label">
              M. Other Credits{" "}
              <span style={{ fontStyle: "italic", fontWeight: 400 }}>
                (Enter the sum of all other credits --- Borrower Paid Fees,
                Earnest Money, Employer Assisted Housing, Lease Purchase Fund,
                Lot Equity, Relocation Funds, Sweat Equity, Other)
              </span>
            </div>
            <DollarInputNumber
              className={`input-number ${getDisabledClassName(
                "dollarinputnumber",
                !editMode
              )}`}
              min={0}
              max={99999999.99}
              value={otherCredits}
              onChange={setOtherCredits}
              disabled={!editMode}
            />
          </div>
          <div className="row-item">
            <div className="label total">
              N. TOTAL CREDITS (Total of L and M)
            </div>
            <DollarInputNumber
              className={`input-number ${getDisabledClassName(
                "dollarinputnumber",
                true
              )}`}
              min={0}
              max={99999999.99}
              value={totalCredits}
              disabled={true}
            />
          </div>
        </div>
        <Line></Line>
        <div className="L4-content-section">
          <div className="title">CALCULATION</div>
          <div className="row-item">
            <div className="label">TOTAL DUE FROM BORROWER(s) (Line H)</div>
            <DollarInputNumber
              className={`input-number ${getDisabledClassName(
                "dollarinputnumber",
                true
              )}`}
              min={0}
              max={99999999.99}
              value={calculationTotalDue}
              disabled={true}
            />
          </div>
          <div className="row-item">
            <div className="label">
              LESS TOTAL MORTGAGE LOANS (Line K) AND TOTAL CREDITS (Line N)
            </div>
            <DollarInputNumber
              className={`input-number ${getDisabledClassName(
                "dollarinputnumber",
                true
              )}`}
              prefix="-$"
              min={0}
              max={99999999.99}
              value={calculationLessTotal}
              disabled={true}
            />
          </div>
          <div className="row-item">
            <div className="label total">
              Cash From/To the Borrower (Line H minus Line K and Line N) NOTE:
              This amount does not include reserves or other funds that may be
              required by the Lender to be verified.
            </div>
            <DollarInputNumber
              className={`input-number ${getDisabledClassName(
                "dollarinputnumber",
                true
              )}`}
              min={0}
              max={99999999.99}
              value={cashFromToBorrower}
              disabled={true}
            />
          </div>
        </div>
      </div>
    );
  };



  return (
    <div id="LenderLoanForm" className="lenderLoanForm">
      <div className="lenderLoanForm-title">
        Uniform Residential Loan Application — Lender Loan Information
      </div>
      <div className="lenderLoanForm-subtitle">
        This section is completed by your Lender.
      </div>

      <div>
        {/* L1 */}
        {L1()}
        {/* L2 */}
        {L2()}
        {/* L3 */}
        {L3()}
        {/* L4 */}
        {L4()}
      </div>
    </div>
  );
})

// 数据结构示例 - 用于自动保存到后端
export interface LenderLoanFormData {
  // L1. Property and Loan Information
  L1: {
    communityPropertyState: string[]; // 社区财产状态 - 枚举值: ["borrower", "property"]
    transactionDetail: string; // 交易详情 - 枚举值: ["conversion", "renovation", "construction"]
    closingType?: string; // 结算类型（仅当transactionDetail为construction时）- 枚举值: ["single-closing", "two-closing"]
    constructionCost: number | null; // 建筑/改善成本
    lotAcquiredDate: string | null; // 地块获得日期
    originalLotCost: number | null; // 地块原始成本
    isRefinance: boolean; // 是否为再融资贷款
    refinanceType?: string; // 再融资类型（仅当isRefinance为true时）- 枚举值: ["no-cash-out", "limited-cash-out", "cash-out"]
    refinanceProgram?: string; // 再融资计划（仅当isRefinance为true时）- 枚举值: ["full-documentation", "interest-rate-reduction", "streamlined-without-appraisal", "other"]
    otherRefinanceProgramExplain?: string; // 其他再融资计划说明
    energyImprovements: string[]; // 能源改善 - 枚举值: ["energy-improvements", "clean-energy-lien"]
    projectType: string; // 项目类型 - 枚举值: ["Condominium", "Cooperative", "planned-unit-development", "property-not-located-in-a-project"]
  };

  // L2. Title Information
  L2: {
    names: string[]; // 产权持有人姓名
    estateType: string; // 地产类型 - 枚举值: ["fee-simple", "leasehold"]
    leaseholdExpirationDate?: string | null; // 租赁到期日期（仅当estateType为leasehold时）
    titleManner: string; // 产权持有方式 - 枚举值: ["sole-ownership", "life-estate", "tenancy-in-common", "joint-tenancy", "tenancy-entirety", "other"]
    isRefinanceLoan: boolean; // 是否为再融资贷款
    namesRefinance: string[]; // 再融资时当前产权持有人姓名
    trustInformation: string; // 信托信息 - 枚举值: ["inter-vivos", "land-trust"]
    indianLandTenure: string; // 印第安土地保有权 - 枚举值: ["fee-simple-reservation", "individual-trust-land", "tribal-trust-reservation", "tribal-trust-off-reservation", "alaska-native-corporation"]
  };

  // L3. Mortgage Loan Information
  L3: {
    mortgageType: string; // 抵押贷款类型 - 枚举值: ["Conventional", "USDA-RD", "FHA", "VA", "other"]
    otherMortgageType?: string; // 其他抵押贷款类型说明（仅当mortgageType为other时）
    amortizationType: string; // 分期付款类型 - 枚举值: ["fixed-rate", "adjustable-rate", "other"]
    otherAmortizationExplain?: string; // 其他分期付款类型说明
    interestOnlyTerm?: number | null; // 仅利息期限（仅当amortizationType为adjustable-rate时）
    subsequentAdjustmentPeriod?: number | null; // 后续调整期（仅当amortizationType为adjustable-rate时）
    loanFeatures: string[]; // 贷款特征 - 枚举值: ["balloon", "interest-only", "negative-amortization", "prepayment-penalty", "buydown", "other-feature"]
    balloonTerm: number | null; // 气球期限
    interestOnlyTermFeature: number | null; // 仅利息期限特征
    negativeAmortizationTerm: number | null; // 负摊销期限
    prepaymentPenaltyTerm: number | null; // 提前还款罚金期限
    buydownRate: number | null; // 买断利率
    otherLoanFeature: string; // 其他贷款特征
    otherLoanFeatureTerm?: number | null; // 其他贷款特征期限
    noteRate: number | null; // 票据利率
    loanTerm: number | null; // 贷款期限
    mortgageLienType: string; // 抵押留置权类型 - 枚举值: ["first-lien", "subordinate-lien"]
    firstMortgagePayment: number | null; // 第一抵押贷款付款
    subordinateLienPayment: number | null; // 次级留置权付款
    homeownersInsurance: number | null; // 房主保险
    supplementalInsurance: number | null; // 补充保险
    propertyTaxes: number | null; // 房产税
    mortgageInsurance: number | null; // 抵押保险
    associationDues: number | null; // 协会会费
    otherMonthlyExpenses: number | null; // 其他月费用
    totalMonthlyPayment: number | null; // 总月付款
  };

  // L4. Qualifying the Borrower
  L4: {
    // DUE FROM BORROWER(S)
    salesContractPrice: number | null; // A. 销售合同价格
    improvementsCost: number | null; // B. 改善、翻新和维修
    landCost: number | null; // C. 土地（如果单独获得）
    refinanceBalance: number | null; // D. 再融资：要在此交易中偿还的房产抵押贷款余额
    creditCardsDebts: number | null; // E. 要偿还的信用卡和其他债务
    borrowerClosingCosts: number | null; // F. 借款人结算成本
    discountPoints: number | null; // G. 折扣点
    totalDueFromBorrower: number | null; // H. 借款人应付总额

    // TOTAL MORTGAGE LOANS
    loanAmountExcludingInsurance: number | null; // I. 不包括融资抵押保险的贷款金额
    financedInsuranceAmount: number | null; // I. 融资抵押保险金额
    otherNewMortgageLoans: number | null; // J. 其他新抵押贷款
    totalMortgageLoans: number | null; // K. 抵押贷款总额

    // TOTAL CREDITS
    sellerCredits: number | null; // L. 卖方信贷
    otherCredits: number | null; // M. 其他信贷
    totalCredits: number | null; // N. 信贷总额

    // CALCULATION
    calculationTotalDue: number | null; // 借款人应付总额（H行）
    calculationLessTotal: number | null; // 减去抵押贷款总额（K行）和信贷总额（N行）
    cashFromToBorrower: number | null; // 借款人现金流入/流出
  };
}

// 数据结构示例
export const lenderLoanFormDataExample: LenderLoanFormData = {
  L1: {
    communityPropertyState: ["borrower"], // 至少有一个借款人居住在社区财产州
    transactionDetail: "construction", // 建筑转换/建筑到永久
    closingType: "single-closing", // 单一结算
    constructionCost: 50000, // $50,000
    lotAcquiredDate: "2024-01-15", // 2024年1月15日
    originalLotCost: 100000, // $100,000
    isRefinance: true, // 是再融资贷款
    refinanceType: "cash-out", // 现金流出
    refinanceProgram: "full-documentation", // 完整文件
    otherRefinanceProgramExplain: "", // 其他说明
    energyImprovements: ["energy-improvements"], // 抵押贷款将资助能源相关改善
    projectType: "Condominium", // 公寓
  },

  L2: {
    names: ["John Smith", "Jane Smith"], // 产权持有人姓名
    estateType: "fee-simple", // 完全所有权
    leaseholdExpirationDate: null, // 租赁到期日期
    titleManner: "joint-tenancy", // 共同所有权，生存者取得权
    isRefinanceLoan: true, // 是再融资贷款
    namesRefinance: ["John Smith", "Jane Smith"], // 再融资时当前产权持有人
    trustInformation: "inter-vivos", // 生前信托
    indianLandTenure: "fee-simple-reservation", // 保留地上的完全所有权
  },

  L3: {
    mortgageType: "Conventional", // 常规贷款
    amortizationType: "adjustable-rate", // 可调利率
    otherAmortizationExplain: "", // 其他分期付款类型说明
    interestOnlyTerm: 60, // 60个月仅利息期
    subsequentAdjustmentPeriod: 12, // 12个月后续调整期
    loanFeatures: ["balloon", "prepayment-penalty"], // 贷款特征
    balloonTerm: 360, // 360个月气球期
    interestOnlyTermFeature: 0, // 仅利息期限特征
    negativeAmortizationTerm: 0, // 负摊销期限
    prepaymentPenaltyTerm: 36, // 36个月提前还款罚金期
    buydownRate: 0, // 买断利率
    otherLoanFeature: "", // 其他贷款特征
    otherLoanFeatureTerm: null, // 其他贷款特征期限
    noteRate: 5.25, // 5.25%票据利率
    loanTerm: 360, // 360个月贷款期限
    mortgageLienType: "first-lien", // 第一留置权
    firstMortgagePayment: 2500, // $2,500第一抵押贷款付款
    subordinateLienPayment: 0, // $0次级留置权付款
    homeownersInsurance: 150, // $150房主保险
    supplementalInsurance: 0, // $0补充保险
    propertyTaxes: 300, // $300房产税
    mortgageInsurance: 100, // $100抵押保险
    associationDues: 200, // $200协会会费
    otherMonthlyExpenses: 50, // $50其他月费用
    totalMonthlyPayment: 3300, // $3,300总月付款
  },

  L4: {
    // DUE FROM BORROWER(S)
    salesContractPrice: 500000, // A. $500,000销售合同价格
    improvementsCost: 50000, // B. $50,000改善、翻新和维修
    landCost: 100000, // C. $100,000土地
    refinanceBalance: 300000, // D. $300,000再融资余额
    creditCardsDebts: 15000, // E. $15,000信用卡和其他债务
    borrowerClosingCosts: 8000, // F. $8,000借款人结算成本
    discountPoints: 2000, // G. $2,000折扣点
    totalDueFromBorrower: 575000, // H. $575,000借款人应付总额

    // TOTAL MORTGAGE LOANS
    loanAmountExcludingInsurance: 450000, // I. $450,000不包括保险的贷款金额
    financedInsuranceAmount: 5000, // I. $5,000融资保险金额
    otherNewMortgageLoans: 0, // J. $0其他新抵押贷款
    totalMortgageLoans: 455000, // K. $455,000抵押贷款总额

    // TOTAL CREDITS
    sellerCredits: 10000, // L. $10,000卖方信贷
    otherCredits: 5000, // M. $5,000其他信贷
    totalCredits: 15000, // N. $15,000信贷总额

    // CALCULATION
    calculationTotalDue: 575000, // $575,000借款人应付总额
    calculationLessTotal: -470000, // -$470,000减去总额
    cashFromToBorrower: 105000, // $105,000借款人现金流入
  },
};

// Redux 连接
const mapStateToProps = (state: any) => {
  return {
    application: state.application,
    status: state.updateStatus?.status || "idle",
    statusSource: state.updateStatus?.source || "application",
    needUpdateLenderLoanForm: state.application?.needUpdateLenderLoanForm || false,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  updateStatus: (status: string, source?: string) => dispatch(updateStatus(status, source)),
  resetNeedUpdateLenderLoanForm: () => dispatch(NeedUpdateLenderLoanForm(false)),
});

export default connect(mapStateToProps, mapDispatchToProps)(LenderLoanForm);
